import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Autocomplete
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { Info as InfoIcon, Delete as DeleteIcon } from '@mui/icons-material';

const EditClient = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const { clientId } = useParams();

  const [clientData, setClientData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [historyEntry, setHistoryEntry] = useState({ date: '', description: '', amount: '' });
  const [clients, setClients] = useState([]);
  const [relation, setRelation] = useState({ client: null, type: '' });

  const relationTypes = [
    'Mąż', 'Żona', 'Syn', 'Córka', 'Partner', 'Partnerka', 'Kolega', 'Koleżanka',
    'Narzeczony', 'Narzeczona', 'Kochanek', 'Kochanka', 'Mama', 'Tata', 'Brat', 'Siostra', 'Inne'
  ];

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const docRef = doc(db, 'clients', clientId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Ensure nested objects exist
          data.socialMedia = data.socialMedia || { instagram: '', facebook: '', tiktok: '', other: '' };
          data.preferences = data.preferences || { ringSize: '', watchBrands: '', goldColor: '', other: '' };
          data.importantDates = data.importantDates || { birthday: '', nameDay: '', anniversary: '' };
          data.purchaseHistory = data.purchaseHistory || [];
          data.relations = data.relations || [];
          setClientData({ id: docSnap.id, ...data });
        } else {
          setSnackbar({ open: true, message: 'Nie znaleziono klienta.', severity: 'error' });
          navigate('/clients');
        }
      } catch (error) {
        console.error("Błąd podczas pobierania danych klienta:", error);
        setSnackbar({ open: true, message: 'Błąd podczas pobierania danych klienta.', severity: 'error' });
      }
    };

    const fetchClientsForRelations = async () => {
      if (!currentUser) return;
      try {
        const q = query(collection(db, 'clients'));
        const querySnapshot = await getDocs(q);
        const clientsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClients(clientsData.filter(c => c.id !== clientId)); // Exclude current client
      } catch (error) {
        console.error("Błąd podczas pobierania klientów do powiązań:", error);
      }
    };

    fetchClient();
    fetchClientsForRelations();
  }, [clientId, navigate, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const keys = name.split('.');

    if (keys.length > 1) {
      setClientData(prev => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: value
        }
      }));
    } else {
      setClientData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdateClient = async () => {
    if (!clientData.firstName || !clientData.lastName) {
      setSnackbar({ open: true, message: 'Imię i nazwisko są wymagane.', severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, {
        ...clientData,
        last_updated_at: serverTimestamp(),
      });

      setSnackbar({ open: true, message: 'Dane klienta zostały zaktualizowane!', severity: 'success' });
      setTimeout(() => navigate('/clients'), 2000);

    } catch (error) {
      console.error("Błąd podczas aktualizacji klienta:", error);
      setSnackbar({ open: true, message: `Błąd: ${error.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleHistoryEntryChange = (e) => {
    setHistoryEntry({ ...historyEntry, [e.target.name]: e.target.value });
  };

  const handleAddHistoryEntry = () => {
    if (historyEntry.description && historyEntry.amount) {
      setClientData(prev => ({
        ...prev,
        purchaseHistory: [...prev.purchaseHistory, historyEntry]
      }));
      setHistoryEntry({ date: '', description: '', amount: '' });
    } else {
      setSnackbar({ open: true, message: 'Opis i kwota w historii zakupów są wymagane.', severity: 'warning' });
    }
  };

  const handleRemoveHistoryEntry = (index) => {
    setClientData(prev => ({
      ...prev,
      purchaseHistory: prev.purchaseHistory.filter((_, i) => i !== index)
    }));
  };

  const handleAddRelation = () => {
    if (relation.client && relation.type) {
      const newRelation = {
        clientId: relation.client.id,
        clientName: `${relation.client.firstName} ${relation.client.lastName}`,
        type: relation.type
      };
      setClientData(prev => ({
        ...prev,
        relations: [...prev.relations, newRelation]
      }));
      setRelation({ client: null, type: '' });
    } else {
      setSnackbar({ open: true, message: 'Wybierz klienta i typ relacji.', severity: 'warning' });
    }
  };

  const handleRemoveRelation = (index) => {
    setClientData(prev => ({
      ...prev,
      relations: prev.relations.filter((_, i) => i !== index)
    }));
  };

  if (!clientData) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Cinzel, serif', textAlign: 'center', mb: 4 }}>
          Edytuj Klienta: {clientData.firstName} {clientData.lastName}
        </Typography>
        
        <Grid container spacing={3}>
          {/* SEKCJA DANE PODSTAWOWE */}
          <Grid item xs={12}>
            <Divider><Chip label="Dane Podstawowe" /></Divider>
          </Grid>
          <Grid item xs={12} md={6}><TextField fullWidth required label="Imię" name="firstName" value={clientData.firstName} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth required label="Nazwisko" name="lastName" value={clientData.lastName} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Email" name="email" type="email" value={clientData.email} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Telefon" name="phone" value={clientData.phone} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Adres" name="address" value={clientData.address} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Miasto" name="city" value={clientData.city} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Zawód" name="profession" value={clientData.profession} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Firma" name="company" value={clientData.company} onChange={handleChange} /></Grid>

          {/* SEKCJA SOCIAL MEDIA */}
          <Grid item xs={12} sx={{ mt: 2 }}><Divider><Chip label="Social Media" /></Divider></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Instagram" name="socialMedia.instagram" value={clientData.socialMedia.instagram} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Facebook" name="socialMedia.facebook" value={clientData.socialMedia.facebook} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="TikTok" name="socialMedia.tiktok" value={clientData.socialMedia.tiktok} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Inne Social Media" name="socialMedia.other" value={clientData.socialMedia.other} onChange={handleChange} /></Grid>

          {/* SEKCJA PREFERENCJE */}
          <Grid item xs={12} sx={{ mt: 2 }}><Divider><Chip label="Preferencje Klienta" /></Divider></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Rozmiar pierścionka" name="preferences.ringSize" value={clientData.preferences.ringSize} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Ulubione marki zegarków" name="preferences.watchBrands" value={clientData.preferences.watchBrands} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Ulubiony kolor złota" name="preferences.goldColor" value={clientData.preferences.goldColor} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Inne preferencje" name="preferences.other" value={clientData.preferences.other} onChange={handleChange} /></Grid>

          {/* SEKCJA WAŻNE DATY */}
          <Grid item xs={12} sx={{ mt: 2 }}><Divider><Chip label="Ważne Daty" /></Divider></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Urodziny" name="importantDates.birthday" type="date" InputLabelProps={{ shrink: true }} value={clientData.importantDates.birthday} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Imieniny" name="importantDates.nameDay" type="date" InputLabelProps={{ shrink: true }} value={clientData.importantDates.nameDay} onChange={handleChange} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="Rocznica" name="importantDates.anniversary" type="date" InputLabelProps={{ shrink: true }} value={clientData.importantDates.anniversary} onChange={handleChange} /></Grid>

          {/* SEKCJA NOTATKI */}
          <Grid item xs={12} sx={{ mt: 2 }}><Divider><Chip label="Notatki" /></Divider></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={4} label="Notatki o kliencie" name="notes" value={clientData.notes} onChange={handleChange} /></Grid>

          {/* SEKCJA HISTORIA ZAKUPÓW */}
          <Grid item xs={12} sx={{ mt: 2 }}><Divider><Chip label="Historia Zakupów" /></Divider></Grid>
          {clientData.purchaseHistory.map((entry, index) => (
            <Grid item xs={12} key={index}>
              <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ flex: 1 }}><strong>Data:</strong> {entry.date}</Typography>
                <Typography sx={{ flex: 2 }}><strong>Opis:</strong> {entry.description}</Typography>
                <Typography sx={{ flex: 1 }}><strong>Kwota:</strong> {entry.amount} PLN</Typography>
                <IconButton onClick={() => handleRemoveHistoryEntry(index)} color="error"><DeleteIcon /></IconButton>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={3}><TextField fullWidth label="Data zakupu" name="date" type="date" InputLabelProps={{ shrink: true }} value={historyEntry.date} onChange={handleHistoryEntryChange} /></Grid>
          <Grid item xs={12} md={5}><TextField fullWidth required label="Opis produktu/usługi" name="description" value={historyEntry.description} onChange={handleHistoryEntryChange} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth required label="Kwota" name="amount" type="number" value={historyEntry.amount} onChange={handleHistoryEntryChange} /></Grid>
          <Grid item xs={12} md={2}><Button fullWidth variant="outlined" onClick={handleAddHistoryEntry} sx={{ height: '100%' }}>Dodaj wpis</Button></Grid>

          {/* SEKCJA POWIĄZANIA */}
          <Grid item xs={12} sx={{ mt: 2 }}><Divider><Chip label="Powiązania z innymi klientami" /></Divider></Grid>
          {clientData.relations.map((rel, index) => (
            <Grid item xs={12} key={index}>
              <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ flex: 2 }}><strong>Klient:</strong> {rel.clientName}</Typography>
                <Typography variant="body2" sx={{ flex: 1 }}><strong>Relacja:</strong> {rel.type}</Typography>
                <IconButton onClick={() => handleRemoveRelation(index)} color="error"><DeleteIcon /></IconButton>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={relation.client}
              onChange={(event, newValue) => setRelation(prev => ({ ...prev, client: newValue }))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Wyszukaj klienta do powiązania" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Typ relacji</InputLabel>
              <Select value={relation.type} label="Typ relacji" onChange={(e) => setRelation(prev => ({ ...prev, type: e.target.value }))}>
                {relationTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}><Button fullWidth variant="outlined" onClick={handleAddRelation} sx={{ height: '100%' }}>Dodaj powiązanie</Button></Grid>

          {/* PRZYCISK ZAPISU */}
          <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" size="large" onClick={handleUpdateClient} disabled={submitting}>
              {submitting ? <CircularProgress size={24} /> : 'Zapisz Zmiany'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditClient;
